import React from 'react';
import * as styles from './styles.css';
import { useAtom } from 'jotai/react';
const Text = React.lazy(() => import('../common/text'));
const Input = React.lazy(() => import('../common/input/base-input'));
import { is_valid_phone_number, is_valid_zipcode } from './helpers';
import { IComponent, IContactInfo } from '../../interfaces';
import { atom } from 'jotai/vanilla';
import { useProps } from '../../hooks/use-props';

export interface IContactInfoProps {
  id: string;
}

export interface IContactInfoAtom extends IComponent {
  value: IContactInfo;
  answer: IContactInfo;
  error: IContactInfo;
}

// Local component atoms
const contact_info = {
  // Primitive atoms containing raw data
  state: atom({
    first_name: '',
    last_name: '',
    zipcode: '',
    email: '',
    phone_number: '',
  }),
  errors: atom({
    first_name: '',
    last_name: '',
    zipcode: '',
    email: '',
    phone_number: '',
  }),
};

const useContactInfo = (id: string) => {
  const [local_state, setLocalState] = useAtom(contact_info.state);
  const [local_errors, setLocalErrors] = useAtom(contact_info.errors);
  const {
    props: state,
    setProps,
    addAnswer,
  } = useProps<IContactInfoAtom>({
    id,
    initial_state: local_state,
  });
  const errors = {
    ...local_errors,
    ...state.error,
  };

  const checkState = async (
    { zipcode, phone_number, first_name, last_name, email }: IContactInfo,
    prop?: string,
  ) => {
    const has_valid_prop = (key: string) => !prop || prop === key;
    const new_errors = { first_name: '', last_name: '', zipcode: '', email: '', phone_number: '' };
    if (has_valid_prop('first_name') && first_name === '') {
      new_errors.first_name = 'Please enter your first name.';
    }
    if (has_valid_prop('last_name') && last_name === '') {
      new_errors.last_name = 'Please enter your last name.';
    }
    const parsed_zipcode = zipcode.replace(/\D/g, '');
    if (has_valid_prop('zipcode')) {
      new_errors.zipcode = await is_valid_zipcode(parsed_zipcode);
    }
    if (has_valid_prop('email') && email === '') {
      new_errors.email = 'Please enter a valid email address.';
    }
    const parsed_phone_number = phone_number.replace(/\D/g, '');
    // Check if phone number is valid
    if (
      has_valid_prop('phone_number') &&
      parsed_phone_number !== '' &&
      parsed_phone_number !== '1' &&
      !is_valid_phone_number(parsed_phone_number)
    ) {
      new_errors.phone_number = 'Please enter a valid phone number.';
    }
    return new_errors;
  };

  const checkErrors = async (prop: string) => {
    const error = await checkState(local_state, prop);
    const errors = { ...local_errors, [prop]: error[prop] };
    setLocalErrors(errors);
    const has_error = Object.values(errors).some((value) => {
      return value !== '';
    });
    setProps({ error: errors, has_error });
  };

  const setState = (new_state: Partial<IContactInfo>) => {
    const updated_state = { ...local_state, ...new_state };
    const all_answers_given = Object.values(updated_state).every((value) => {
      return !!value;
    });
    1;
    const no_errors = Object.keys(updated_state).every((key) => checkState(updated_state, key)); // Checks if there's an error but doesn't update the message since onBlur will do it
    const can_submit = all_answers_given && no_errors;
    setLocalState(updated_state);
    if (!can_submit) return addAnswer({ createApplicant: undefined });
    // Unmask phone number and reformat into the format VTTA expects (e.g. +15555555555)
    const phone_number = `+${updated_state.phone_number.replace(/\D/g, '')}`;
    addAnswer({
      createApplicant: {
        ...updated_state,
        phone_number,
      },
    });
  };

  return {
    state,
    setState,
    errors,
    checkErrors,
  };
};

const ContactInfo = ({ id }: IContactInfoProps) => {
  const {
    state: { show, value, in_review },
    setState,
    errors,
    checkErrors,
  } = useContactInfo(id);
  if (!show) return null;
  return (
    <div id={id} className={styles.root}>
      <Text id={`${id}-text`} title={'Contact Information'}>
        Thank you for your interest in tutoring! By filling out this application you&apos;re just a
        few steps away from supporting learners.
        {/*This information will be used to facilitate your*/}
        {/*background check. Varsity Tutors completes the &quot;*/}
        {/*<a*/}
        {/*    href="https://help.checkr.com/hc/en-us/articles/8789699142551-Available-packages-and-add-ons"*/}
        {/*    target="_blank"*/}
        {/*    className={styles.link}*/}
        {/*    rel="noreferrer"*/}
        {/*>*/}
        {/*  Essential Package*/}
        {/*</a>*/}
        {/*&quot; via Checkr for all tutors on its platform.*/}
      </Text>
      <div className={styles.inputBox}>
        <Input
          id={`${id}-first_name`}
          label={'First Name'}
          value={value.first_name}
          placeholder={'Legal First Name'}
          onChange={(first_name) => {
            setState({ first_name });
          }}
          onBlur={() => checkErrors('first_name')}
          error={errors.first_name}
        />
        <Input
          id={`${id}-last_name`}
          label={'Last Name'}
          value={value.last_name}
          placeholder={'Legal Last Name'}
          onChange={(last_name) => {
            setState({ last_name });
          }}
          onBlur={() => checkErrors('last_name')}
          error={errors.last_name}
        />
        <Input
          id={`${id}-zip_code`}
          label={'ZIP Code'}
          value={value.zipcode}
          placeholder={'ZIP Code'}
          onChange={(zipcode) => {
            setState({ zipcode });
          }}
          onBlur={() => checkErrors('zipcode')}
          mask={'99999'}
          error={errors.zipcode}
        />
        <Input
          id={`${id}-email`}
          label={'Email'}
          value={value.email}
          disabled={in_review}
          placeholder={'name@example.com'}
          onChange={(email) => {
            setState({ email });
          }}
          onBlur={() => checkErrors('email')}
          error={errors.email}
        />
        <Input
          id={`${id}-phone_number`}
          label={'Phone Number'}
          placeholder={'Phone Number'}
          value={value.phone_number}
          type={'tel'}
          onChange={(phone_number) => {
            if (phone_number === '1') {
              // If the new input is just 1, then the phone has been cleared
              setState({ phone_number: '' });
              return;
            }
            setState({ phone_number });
          }}
          onBlur={() => checkErrors('phone_number')}
          mask="+1 (999) 999-9999"
          error={errors.phone_number}
        />
      </div>
    </div>
  );
};

export default ContactInfo;
